import { useState, useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { transformProductImages, isNewImage } from '../lib/imageHelpers';
import { processImageChanges } from '../lib/imageTracking';
import { useProduct } from './useProduct';
import { transformBrandsToOptions } from '../lib/brandHelpers';
import { useCatalogHierarchy } from '../../../../hooks/useCatalogHierarchy';

/**
 * @typedef {Object} ProductSuggestions
 * @property {string} [brandName] - Suggested brand name
 * @property {string} [domain] - Domain where product was found
 * @property {number} [categoryId] - Suggested category ID
 * @property {string} [title] - Suggested product title
 * @property {string[]} [images] - Array of suggested image URLs
 */

/**
 * @typedef {Object} FormState
 * @property {Object} selectedCategory - Selected category option
 * @property {Function} setSelectedCategory - Category setter
 * @property {string} title - Product title
 * @property {Function} setTitle - Title setter
 * @property {Array} images - Product images
 * @property {Function} setImages - Images setter
 * @property {boolean} isHidden - Hidden from rankings flag
 * @property {Function} setIsHidden - isHidden setter
 * @property {boolean} isSeoPrerendered - SEO pre-render flag
 * @property {Function} setIsSeoPrerendered - isSeoPrerendered setter
 */

/**
 * @param {number} productId - Product ID for editing
 * @param {ProductSuggestions} suggestions - Suggested values for new product
 * @returns {FormState} Complete form state and handlers
 */
export const useProductForm = (productId, suggestions = {}) => {
  const isEditMode = !!productId;

  const { brands, categoriesById: categories, departmentsById: departments } = useCatalogHierarchy();

  const [imageChanges, setImageChanges] = useState({
    added: [],
    modified: [],
    deleted: []
  });

  const resetImageChanges = () => {
    setImageChanges({
      added: [],
      modified: [],
      deleted: []
    });
  };

  const trackImageAdded = useCallback(
    newImage => {
      if (isEditMode) {
        setImageChanges(prev => {
          const existingIndex = prev.added.findIndex(img => img.id === newImage.id);

          if (existingIndex >= 0) {
            // Update existing entry with new URL/data
            const updated = [...prev.added];
            updated[existingIndex] = newImage;
            return { ...prev, added: updated };
          } else {
            // Add new entry
            return { ...prev, added: [...prev.added, newImage] };
          }
        });
      }
    },
    [isEditMode]
  );

  const trackImageModified = useCallback(
    (imageId, imageData) => {
      if (isEditMode) {
        setImageChanges(prev => {
          const existingModifiedIndex = prev.modified.findIndex(img => img.id === imageId);

          if (existingModifiedIndex >= 0) {
            const modified = [...prev.modified];
            modified[existingModifiedIndex] = imageData;
            return { ...prev, modified };
          } else {
            return { ...prev, modified: [...prev.modified, imageData] };
          }
        });
      }
    },
    [isEditMode]
  );

  const trackImageDeleted = useCallback(
    imageId => {
      if (isEditMode) {
        setImageChanges(prev => {
          const shouldTrackDeletion = !isNewImage(imageId);

          return {
            ...prev,
            deleted: shouldTrackDeletion ? [...prev.deleted, imageId] : prev.deleted,
            added: prev.added.filter(img => img.id !== imageId),
            modified: prev.modified.filter(img => img.id !== imageId)
          };
        });
      }
    },
    [isEditMode]
  );

  const findSuggestedBrand = () => {
    if (!productId && suggestions.brandName) {
      const matchingBrand = _.find(brands, b => b.name === suggestions.brandName);
      if (matchingBrand) {
        return { value: matchingBrand.id, label: matchingBrand.name };
      }
    }
    return null;
  };
  const [selectedBrand, setSelectedBrand] = useState(findSuggestedBrand);
  const [userCreatedBrand, setUserCreatedBrand] = useState(false);
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);

  const brandOptions = useMemo(() => transformBrandsToOptions(brands), [brands]);

  const handleBrandChange = option => {
    setSelectedBrand(option);
    setUserCreatedBrand(false);
  };

  const handleNewBrandSelect = brand => {
    setSelectedBrand(brand);
    setUserCreatedBrand(true);
  };

  const handleConflictBrandSelect = brand => {
    setSelectedBrand(brand);
    setUserCreatedBrand(false);
  };

  const openAddBrandModal = () => {
    setShowAddBrandModal(true);
  };

  const closeAddBrandModal = () => {
    setShowAddBrandModal(false);
  };

  const findSuggestedCategory = () => {
    const categoryId = productId ? null : suggestions.categoryId;

    if (!categoryId) {
      return null;
    }

    const matchingCategory = categories?.[categoryId];

    if (matchingCategory) {
      return {
        value: matchingCategory.id,
        label: matchingCategory.name
      };
    }

    return null;
  };
  const [selectedCategory, setSelectedCategory] = useState(findSuggestedCategory);

  // The catalog hierarchy loads lazily, so the first mount can read an empty brand and category map.
  useEffect(() => {
    setSelectedBrand(current => current ?? findSuggestedBrand());
    setSelectedCategory(current => current ?? findSuggestedCategory());
  }, [brands, categories]);

  const [title, setTitle] = useState(() => {
    if (!productId && suggestions.title) {
      return _.startCase(_.toLower(suggestions.title));
    }
    return '';
  });

  const [images, setImagesState] = useState(() => {
    if (!productId && suggestions.images) {
      return transformProductImages(suggestions.images);
    }
    return [];
  });

  const [isHidden, setIsHidden] = useState(false);
  const [isSeoPrerendered, setIsSeoPrerendered] = useState(false);

  // Description states
  const [description, setDescription] = useState('');
  const [descriptionWhyBuy, setDescriptionWhyBuy] = useState('');
  const [descriptionWhatPeopleSay, setDescriptionWhatPeopleSay] = useState('');
  const [isDescriptionManual, setIsDescriptionManual] = useState(false);
  const [isDescriptionWhyBuyManual, setIsDescriptionWhyBuyManual] = useState(false);
  const [isDescriptionWhatPeopleSayManual, setIsDescriptionWhatPeopleSayManual] = useState(false);

  const setImages = useCallback(
    updater => {
      setImagesState(prevImages => {
        const newImages = typeof updater === 'function' ? updater(prevImages) : updater;

        if (isEditMode) {
          return processImageChanges(prevImages, newImages, {
            trackImageDeleted,
            trackImageModified,
            trackImageAdded
          });
        }

        return newImages;
      });
    },
    [isEditMode, trackImageDeleted, trackImageModified, trackImageAdded]
  );

  const { isSaving, isLoading, loadError, save: saveProduct, duplicateProduct, setDuplicateProduct } = useProduct(
    productId,
    resetImageChanges,
    setImages,
    setTitle,
    setSelectedBrand,
    setSelectedCategory,
    setIsHidden,
    setIsSeoPrerendered,
    categories,
    setDescription,
    setDescriptionWhyBuy,
    setDescriptionWhatPeopleSay,
    setIsDescriptionManual,
    setIsDescriptionWhyBuyManual,
    setIsDescriptionWhatPeopleSayManual
  );

  // A duplicate found on Save was for the title and brand as they were then.
  useEffect(() => {
    setDuplicateProduct(null);
  }, [title, selectedBrand?.value]);

  const handleSave = onSave => {
    const formData = {
      title,
      selectedBrand,
      selectedCategory,
      images,
      isHidden,
      isSeoPrerendered,
      description,
      descriptionWhyBuy,
      descriptionWhatPeopleSay,
      isDescriptionManual,
      isDescriptionWhyBuyManual,
      isDescriptionWhatPeopleSayManual
    };

    saveProduct(formData, imageChanges, onSave, userCreatedBrand);
  };

  const categoryOptions = useMemo(() => {
    const departmentValues = _.values(departments);
    const categoryValues = _.values(categories);
    return _.orderBy(departmentValues, 'name').map(department => {
      const depCats = _.orderBy(
        _.filter(categoryValues, c => c?.Department_id === department.id),
        'name'
      );
      return {
        label: department.name,
        options: depCats.map(category => ({
          value: category.id,
          label: category.name
        }))
      };
    });
  }, [departments, categories]);

  return {
    // Form state
    selectedCategory,
    setSelectedCategory,
    title,
    setTitle,
    images,
    setImages,
    isHidden,
    setIsHidden,
    isSeoPrerendered,
    setIsSeoPrerendered,

    // Description state
    description,
    setDescription,
    descriptionWhyBuy,
    setDescriptionWhyBuy,
    descriptionWhatPeopleSay,
    setDescriptionWhatPeopleSay,
    isDescriptionManual,
    setIsDescriptionManual,
    isDescriptionWhyBuyManual,
    setIsDescriptionWhyBuyManual,
    isDescriptionWhatPeopleSayManual,
    setIsDescriptionWhatPeopleSayManual,

    // Brand state
    selectedBrand,
    userCreatedBrand,
    showAddBrandModal,
    brandOptions,
    handleBrandChange,
    handleNewBrandSelect,
    handleConflictBrandSelect,
    openAddBrandModal,
    closeAddBrandModal,

    // Save state
    isSaving,
    handleSave,
    duplicateProduct,

    // Loading state
    isLoading,
    loadError,

    // Options
    categoryOptions,

    // Meta
    isEditMode
  };
};
